import React, { useEffect, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { GetServiceProviderUnitOverviewResponse, ServiceProviderUnitCitizenModel, TrafficLightZone } from '../../api-service/clients';
import { WindowService } from '../../components/window-service/window-service';
import { formatCpr, formatDate } from '../../utilities';
import { useServiceProviderUnits } from '../service-provider-unit-calendar/hooks/use-service-provider-units';
import { ServiceProviderRoute, ServiceProviderUrlBuilder } from '../citizen/serviceprovider/utils/service-provider-url-builder';
import { ZoneChip } from '../citizen/serviceprovider/traffic-light/zone-chip';
import { getServiceProviderUnitOverview } from './service-provider-unit-overview-api';
import { UnitPatternsCard } from './unit-patterns-card';

type SortKey = 'attention' | 'name' | 'zone' | 'zoneEscalations' | 'useOfForce' | 'staleObjectives' | 'obsNotes';

const zoneSeverity: Record<TrafficLightZone, number> = {
  [TrafficLightZone.Green]: 1,
  [TrafficLightZone.Yellow]: 2,
  [TrafficLightZone.Red]: 3,
};

function severity(citizen: ServiceProviderUnitCitizenModel) {
  return citizen.zone ? zoneSeverity[citizen.zone] : 0;
}

const compareBy: Record<SortKey, (a: ServiceProviderUnitCitizenModel, b: ServiceProviderUnitCitizenModel) => number> = {
  attention: (a, b) => severity(b) - severity(a) || b.useOfForceIncidents - a.useOfForceIncidents || b.zoneEscalations - a.zoneEscalations || byName(a, b),
  name: (a, b) => byName(a, b),
  zone: (a, b) => severity(b) - severity(a) || byName(a, b),
  zoneEscalations: (a, b) => b.zoneEscalations - a.zoneEscalations || byName(a, b),
  useOfForce: (a, b) => b.useOfForceIncidents - a.useOfForceIncidents || b.unfinishedUseOfForceIncidents - a.unfinishedUseOfForceIncidents || byName(a, b),
  staleObjectives: (a, b) => b.partialObjectivesWithoutRecentFollowup - a.partialObjectivesWithoutRecentFollowup || byName(a, b),
  obsNotes: (a, b) => b.activeObsNotes - a.activeObsNotes || byName(a, b),
};

function byName(a: ServiceProviderUnitCitizenModel, b: ServiceProviderUnitCitizenModel) {
  return (a.citizenName ?? '').localeCompare(b.citizenName ?? '', 'da');
}

export function ServiceProviderUnitOverview() {
  const { currentUnit } = useServiceProviderUnits();
  const serviceProviderUnitId = currentUnit?.id;
  const [overview, setOverview] = useState<GetServiceProviderUnitOverviewResponse>();
  const [sortKey, setSortKey] = useState<SortKey>('attention');
  const [highlightedCitizenIds, setHighlightedCitizenIds] = useState<string[]>([]);

  useEffect(() => {
    if (!serviceProviderUnitId) return undefined;
    let isCurrent = true;
    setOverview(undefined);
    setHighlightedCitizenIds([]);
    async function load(unitId: string) {
      const response = await getServiceProviderUnitOverview(unitId);
      if (isCurrent) setOverview(response);
    }
    load(serviceProviderUnitId);
    return () => {
      isCurrent = false;
    };
  }, [serviceProviderUnitId]);

  if (!serviceProviderUnitId || !overview) return null;

  const { canReadUseOfForce, canReadPartialObjectives } = overview;
  const citizens = [...overview.citizens].sort(compareBy[sortKey]);

  function openCitizen(citizen: ServiceProviderUnitCitizenModel) {
    WindowService.open(ServiceProviderUrlBuilder.build(ServiceProviderRoute.DETAILS, { citizenId: citizen.citizenId, spuId: serviceProviderUnitId }));
  }

  function sortHeader(key: SortKey, label: string) {
    return (
      <TableSortLabel active={sortKey === key} direction="desc" onClick={() => setSortKey(sortKey === key ? 'attention' : key)}>
        {label}
      </TableSortLabel>
    );
  }

  return (
    <>
      <UnitKeyFigures
        citizens={overview.citizens}
        lookbackDays={overview.lookbackDays}
        canReadUseOfForce={canReadUseOfForce}
        canReadPartialObjectives={canReadPartialObjectives}
      />
      {canReadUseOfForce && citizens.length > 0 && <UnitPatternsCard key={serviceProviderUnitId} serviceProviderUnitId={serviceProviderUnitId} onHighlightCitizens={setHighlightedCitizenIds} />}
      {citizens.length === 0 ? (
        <Typography variant="body2">Der er ingen borgere med en aktuel bestilling på enheden.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{sortHeader('name', 'Borger')}</TableCell>
              <TableCell>{sortHeader('zone', 'Trafiklys')}</TableCell>
              <TableCell>{sortHeader('zoneEscalations', `Skift til gul/rød (${overview.lookbackDays} dage)`)}</TableCell>
              {canReadUseOfForce && <TableCell>{sortHeader('useOfForce', `Magtanvendelser (${overview.lookbackDays} dage)`)}</TableCell>}
              {canReadPartialObjectives && <TableCell>{sortHeader('staleObjectives', 'Delmål uden opfølgning')}</TableCell>}
              <TableCell>{sortHeader('obsNotes', 'Obs-noter')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {citizens.map(citizen => (
              <TableRow key={citizen.citizenId} hover selected={highlightedCitizenIds.includes(citizen.citizenId)} onClick={() => openCitizen(citizen)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                  {citizen.citizenName ?? '-'}
                  {citizen.citizenCpr && (
                    <Typography variant="caption" display="block">
                      {formatCpr(citizen.citizenCpr)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {citizen.zone ? (
                    <>
                      <ZoneChip zone={citizen.zone} small />
                      {citizen.zoneSince && (
                        <Typography variant="caption" display="block">
                          siden {formatDate(citizen.zoneSince)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="caption">Ikke udfyldt</Typography>
                  )}
                </TableCell>
                <TableCell>{citizen.zoneEscalations || '-'}</TableCell>
                {canReadUseOfForce && (
                  <TableCell>
                    {citizen.useOfForceIncidents || '-'}
                    {citizen.unfinishedUseOfForceIncidents > 0 && (
                      <Typography variant="caption" display="block">
                        {citizen.unfinishedUseOfForceIncidents} uafsluttede
                      </Typography>
                    )}
                  </TableCell>
                )}
                {canReadPartialObjectives && (
                  <TableCell>
                    {citizen.currentPartialObjectives === 0 ? (
                      <Typography variant="caption">Ingen aktuelle delmål</Typography>
                    ) : (
                      `${citizen.partialObjectivesWithoutRecentFollowup} af ${citizen.currentPartialObjectives}`
                    )}
                  </TableCell>
                )}
                <TableCell>{citizen.activeObsNotes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function UnitKeyFigures(props: {
  citizens: ServiceProviderUnitCitizenModel[];
  lookbackDays: number;
  canReadUseOfForce: boolean;
  canReadPartialObjectives: boolean;
}) {
  const { citizens, lookbackDays, canReadUseOfForce, canReadPartialObjectives } = props;
  const inZone = (zone: TrafficLightZone) => citizens.filter(citizen => citizen.zone === zone).length;
  const figures = [
    { label: 'Borgere', value: citizens.length },
    { label: 'I rød zone', value: inZone(TrafficLightZone.Red) },
    { label: 'I gul zone', value: inZone(TrafficLightZone.Yellow) },
    { label: 'I grøn zone', value: inZone(TrafficLightZone.Green) },
    ...(canReadUseOfForce ? [{ label: `Magtanvendelser seneste ${lookbackDays} dage`, value: citizens.reduce((sum, citizen) => sum + citizen.useOfForceIncidents, 0) }] : []),
    ...(canReadPartialObjectives
      ? [{ label: `Delmål uden opfølgning i ${lookbackDays} dage`, value: citizens.reduce((sum, citizen) => sum + citizen.partialObjectivesWithoutRecentFollowup, 0) }]
      : []),
  ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      {figures.map(figure => (
        <Paper key={figure.label} variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 140 }}>
          <Typography variant="h5">{figure.value}</Typography>
          <Typography variant="caption">{figure.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}
